import * as zod from 'zod';

import { CartesianPose } from '@sb/geometry';
import {
  type ChessboardParameters,
  CameraIntrinsics,
  type ExtrinsicsType,
  type CameraIntrinsicsExtended,
  type CalibrationCoverageResult,
} from '@sb/integrations/types/cameraTypes';
import type {
  DetectScenePlaneRequest,
  DetectScenePlaneResponse,
  RunProjectionRequest,
  RunProjectionResponse,
  Run3DPoseEstimationRequest,
  Run3DPoseEstimationResponse,
  ComputeDinoEmbeddingsRequest,
  ComputeDinoEmbeddingsResponse,
  ComputeDinoImageEmbeddingRequest,
  ComputeDinoImageEmbeddingResponse,
  ComputeOrientationReferenceRequest,
  ComputeOrientationReferenceResponse,
  SegmentMaskRequest,
  SegmentMaskResponse,
  TrainLocateHeadRequest,
  TrainLocateHeadResponse,
  PreloadRoboflowWorkflowRequest,
  PreloadRoboflowWorkflowResponse,
  RunRoboflowWorkflowDetectionRequest,
  RunRoboflowWorkflowDetectionResponse,
} from '@sb/messenger/auto_generated/messenger/vision_bot_service';

import type {
  LocateMatch,
  Blob2DParams,
  Shape2DParams,
  TemplateImage,
  TextPromptDetection2DParams,
} from './LocateTypes';
import {
  RegionOfInterest,
  DEFAULT_REGION_OF_INTEREST,
} from './visionConfigSchemas';

export type { ChessboardParameters };

// Re-exported from the geometry-free `visionConfigSchemas` leaf so the
// skill-config layer (`@sb/ai`) can consume `RegionOfInterest` without
// dragging in `@sb/geometry`/`three`. See visionConfigSchemas.ts.
export { RegionOfInterest, DEFAULT_REGION_OF_INTEREST };

export const Point2D = zod.object({ x: zod.number(), y: zod.number() });
export type Point2D = zod.infer<typeof Point2D>;

export const ClassifyClass = zod.object({
  name: zod.string(),
  storageID: zod.string(),
});

export type ClassifyClass = zod.infer<typeof ClassifyClass>;

export const ClassifyResult = zod.object({
  name: zod.string(),
  score: zod.number(),
});

export type ClassifyResult = zod.infer<typeof ClassifyResult>;

export const CalibrationEntry = zod.object({
  storageID: zod.string(), // image of chessboard
  wristPose: CartesianPose,
});

export type CalibrationEntry = zod.infer<typeof CalibrationEntry>;

export const CalculateIntrinsicsDeprecatedResult = zod.object({
  intrinsics: CameraIntrinsics,
  wristToCameraTransform: CartesianPose.nullable(),
});

export type CalculateIntrinsicsDeprecatedResult = zod.infer<
  typeof CalculateIntrinsicsDeprecatedResult
>;

export const ChessboardCornersResult = zod.object({
  isDetected: zod.boolean(),
  corners: zod.array(Point2D),
});

export type ChessboardCornersResult = zod.infer<typeof ChessboardCornersResult>;

export interface VisionInterface {
  classify(
    classes: ClassifyClass[],
    currentFrame: ArrayBuffer,
    regionOfInterest: RegionOfInterest,
  ): Promise<ClassifyResult[]>;
  detect2DBlobs(
    image: ArrayBuffer,
    regionOfInterest: RegionOfInterest,
    params: Omit<Blob2DParams, 'disableRotation'>,
  ): Promise<LocateMatch[]>;
  detect2DShapes(
    image: ArrayBuffer,
    templateImage: TemplateImage,
    regionOfInterest: RegionOfInterest,
    params: Omit<Shape2DParams, 'disableRotation'>,
  ): Promise<LocateMatch[]>;
  detect2DTextPrompt(
    image: ArrayBuffer,
    regionOfInterest: RegionOfInterest,
    params: TextPromptDetection2DParams,
  ): Promise<LocateMatch[]>;
  preprocessImage(
    image: ArrayBuffer,
    regionOfInterest: RegionOfInterest,
  ): Promise<{
    croppedImage: ArrayBuffer;
    offsetX: number;
    offsetY: number;
  }>;
  getChessboardCorners(
    image: ArrayBuffer,
    chessboard: ChessboardParameters,
  ): Promise<ChessboardCornersResult>;
  getCameraChessboardTransform(
    image: ArrayBuffer,
    chessboard: ChessboardParameters,
    // Extended: the PnP solve needs the distortion model to place the board
    // correctly. `Camera.getIntrinsics()` already returns this type.
    intrinsics: CameraIntrinsicsExtended,
  ): Promise<CartesianPose>;
  calculateIntrinsicsDeprecated(
    calibration: CalibrationEntry[],
    chessboard: ChessboardParameters,
  ): Promise<CalculateIntrinsicsDeprecatedResult>;
  calibrateCameraIntrinsics(
    calibration: CalibrationEntry[],
    chessboard: ChessboardParameters,
  ): Promise<CameraIntrinsicsExtended>;
  calibrateCameraExtrinsics(
    calibration: CalibrationEntry[],
    chessboard: ChessboardParameters,
    intrinsics: CameraIntrinsics,
    extrinsicsType: ExtrinsicsType,
  ): Promise<CartesianPose>;
  assessCalibrationCoverage(
    calibration: CalibrationEntry[],
    chessboard: ChessboardParameters,
    calibrationType: 'intrinsic' | 'extrinsic',
    intrinsics?: CameraIntrinsicsExtended,
    mountType?: 'base' | 'tcp',
  ): Promise<CalibrationCoverageResult>;
  runProjection(request: RunProjectionRequest): Promise<RunProjectionResponse>;
  detectScenePlane(
    request: DetectScenePlaneRequest,
  ): Promise<DetectScenePlaneResponse>;
  run3DPoseEstimation(
    request: Run3DPoseEstimationRequest,
    options?: Run3DPoseEstimationOptions,
  ): Promise<Run3DPoseEstimationResponse>;
  computeDinoEmbeddings(
    request: ComputeDinoEmbeddingsRequest,
  ): Promise<ComputeDinoEmbeddingsResponse>;
  computeDinoImageEmbedding(
    request: ComputeDinoImageEmbeddingRequest,
  ): Promise<ComputeDinoImageEmbeddingResponse>;
  trainLocateHead(
    request: TrainLocateHeadRequest,
  ): Promise<TrainLocateHeadResponse>;
  /**
   * Fetch a Roboflow workflow's definition and weights into the local
   * inference container's cache.
   *
   * On the same vision-bot RPC a GPU task runner calls. A runner is a REMOTE
   * vision-bot, so the only thing it adds here is which host answers — the
   * robot's own vision-bot serves this too, which is what lets an Inspect skill
   * run on a box with no runner and no discrete GPU.
   *
   * The API key is NOT a parameter. vision-bot needs one on every call — it may
   * run where no robot filesystem is — but a caller in the routine runner has no
   * business holding the robot's credential, so the client supplies it. The
   * runner path splits it the same way, handing `GPUTaskRunner` a reader at
   * construction.
   */
  preloadRoboflowWorkflow(
    request: Omit<PreloadRoboflowWorkflowRequest, 'apiKey'>,
  ): Promise<PreloadRoboflowWorkflowResponse>;
  /** Run a preloaded workflow on one frame. See `preloadRoboflowWorkflow`. */
  runRoboflowWorkflowDetection(
    request: Omit<RunRoboflowWorkflowDetectionRequest, 'apiKey'>,
  ): Promise<RunRoboflowWorkflowDetectionResponse>;
  /**
   * Fetch the bytes of a stored image by its storageID. Used by step methods
   * that persist references to captured frames (e.g. DinoFastClassify lazily
   * re-encoding stale example embeddings).
   */
  fetchStoredImage(storageID: string): Promise<ArrayBuffer>;
  segmentMask(request: SegmentMaskRequest): Promise<SegmentMaskResponse>;
  computeOrientationReference(
    request: ComputeOrientationReferenceRequest,
  ): Promise<ComputeOrientationReferenceResponse>;
}

export interface Run3DPoseEstimationOptions {
  /**
   * Storage ID of a user-uploaded CAD model (STL/OBJ/PLY). When set, the
   * concrete VisionInterface implementation reads the file from storage and
   * populates `request.objectReference.cadModelFile` before forwarding to
   * vision-bot. Ignored if `request.objectReference` already carries a value.
   */
  cadModelStorageID?: string;
  cadModelFormat?: 'STL' | 'OBJ' | 'PLY';
  cadModelUnits?: 'METERS' | 'MILLIMETERS' | 'CENTIMETERS' | 'INCHES';
}
